import {Helmet} from '@dr.pogodin/react-helmet';
import {Card, Form, FormField, FormSubmitHandler, showNotification} from 'platform/components';
import {Center, VStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useCreateWorkshopInterestMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {interestRoutes, testIds, workshopRoutes} from '@dms/routes';
import {
  CustomerWidgetCard,
  EntityHeader,
  handleApiError,
  Page,
  Section,
  useCodeList,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {
  WorkshopInterestCreateFormSchemaType,
  workshopInterestCreateFormSchema,
} from './WorkshopInterestCreateFormSchema';

export function WorkshopInterestCreate() {
  const navigate = useNavigate();

  const [createWorkshopInterest, {isLoading: isCreateLoading}] =
    useCreateWorkshopInterestMutation();

  const [workshopInterestSourcesCodelist] = useCodeList('workshop_interest_source');

  const navigateToDetail = (interestId: string) => {
    navigate(
      composePath(workshopRoutes.interestDetailOverview, {
        params: {id: interestId},
      })
    );
  };

  const handleSubmit: FormSubmitHandler<WorkshopInterestCreateFormSchemaType> = async (values) => {
    await createWorkshopInterest({
      createWorkshopInterestRequestBody: {
        sourceCodeId: values.sourceCodeId[0],
        customerId: values.customer?.id ?? '',
      },
    })
      .unwrap()
      .then((response) => {
        if (!response?.id) {
          showNotification.error();
          return;
        }

        navigateToDetail(response.id);
      })
      .catch(handleApiError);
  };

  return (
    <>
      <Helmet title={i18n.t('entity.interest.labels.newInterest')} />
      <Form<WorkshopInterestCreateFormSchemaType>
        experimentalZodSchema={workshopInterestCreateFormSchema}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => (
          <Page
            data-testid={testIds.workshop.interestCreate('page')}
            isFullHeight
            header={
              <EntityHeader
                variant="create"
                data-testid={testIds.workshop.interestCreate('header')}
                title={i18n.t('entity.interest.labels.newInterest')}
                actions={[
                  {
                    'data-testid': testIds.workshop.interestCreate('header-discard'),
                    type: 'button',
                    variant: 'secondary',
                    title: i18n.t('general.actions.discard'),
                    isDisabled: isCreateLoading,
                    onClick: () => navigate(interestRoutes.workshopOverview),
                  },
                  {
                    'data-testid': testIds.workshop.interestCreate('header-create'),
                    type: 'form-button',
                    variant: 'primary',
                    title: i18n.t('general.actions.create'),
                    control,
                    isDisabled:
                      isNilOrEmpty(formApi.watch('sourceCodeId')) ||
                      isNilOrEmpty(formApi.watch('customer')?.id),
                    isLoading: isCreateLoading,
                    buttonType: 'submit',
                  },
                ]}
                flags={[{label: i18n.t('entity.interest.labels.concept'), colorScheme: 'neutral'}]}
                icon="content/next_week"
              />
            }
          >
            <Section>
              <Center>
                <VStack spacing={4} maxWidth={166} width="100%">
                  <Card title={i18n.t('entity.interest.labels.interest')}>
                    <VStack spacing={4}>
                      <FormField
                        data-testid={testIds.workshop.interestCreate('source')}
                        control={control}
                        label={i18n.t('entity.interest.labels.source')}
                        name="sourceCodeId"
                        type="chips"
                        options={
                          workshopInterestSourcesCodelist?.map((item) => ({
                            label: item.name,
                            value: item.codeId,
                          })) ?? []
                        }
                      />
                    </VStack>
                  </Card>
                  <CustomerWidgetCard
                    data-testid={testIds.workshop.interestCreate('customer')}
                    isContactPersonHidden
                    canChangeCustomer
                    secondStepComponentType="BUSINESS_CASE"
                    customerId={formApi.watch('customer')?.id}
                    onChange={() => formApi.setValue('customer', null)}
                    onCustomer={(customer) => formApi.setValue('customer', customer)}
                  />
                </VStack>
              </Center>
            </Section>
          </Page>
        )}
      </Form>
    </>
  );
}
